/**
 * True when a stored config represents a Balanced build (a metric is set to 100).
 * A Fleet-savings build has `strategy: null` (defensively, an all-zero strategy
 * is also treated as not-balanced).
 */
export function isBalancedStrategy(
  strategy: uui.domain.actions.rpc.route.BalancingStrategy | null | undefined,
): boolean {
  if (!strategy) return false
  return (
    strategy.ordersCount === 100 ||
    strategy.workingTime === 100 ||
    strategy.traveledDistance === 100 ||
    strategy.load === 100
  )
}

/**
 * Map a Custom Build configuration to a `buildRoutes` wire payload. The optimizer
 * version is decided entirely by the backend, so it is not sent.
 */
export function configToPayload(
  config: uui.domain.server.rm.CustomBuildConfiguration,
  skipMinRequirements: boolean,
): uui.domain.actions.rpc.route.BuildRoutesPayload {
  return {
    minimizeVehicles: config.minimizeVehicles,
    skipMinRequirements,
    balancing: config.strategy ? { strategy: { ...config.strategy } } : null,
  }
}

/**
 * Translated label-map required by `strategyLabelFromConfig`. Structurally matches
 * (a subset of) `RouteOptimizationModal`'s `useTexts()` — callers with hook access
 * pass that hook's return value straight through.
 */
export type StrategyLabelTexts = {
  fleetSavings: string
  balancedPerformance: string
  configMinDrivers: string
  configAllDrivers: string
  configBalanceOrderDistribution: string
  configBalanceWorkingTime: string
  configBalanceDistance: string
  configBalanceLoadDistribution: (loadName: string) => string
}

// Pure helper — no hook access, so the caller supplies already-translated strings
// (see `buildStrategyLabel` in RouteOptimizationModal/hooks/useControllerActions.ts
// for the sibling implementation building the same kind of label from modal state).
export function strategyLabelFromConfig(
  config: uui.domain.server.rm.CustomBuildConfiguration,
  texts: StrategyLabelTexts,
): string {
  const driver = config.minimizeVehicles ? texts.configMinDrivers : texts.configAllDrivers
  const s = config.strategy
  if (!s || !isBalancedStrategy(s)) return `${texts.fleetSavings}: ${driver}`
  if (s.ordersCount === 100) {
    return `${texts.balancedPerformance}: ${texts.configBalanceOrderDistribution} (${driver})`
  }
  if (s.workingTime === 100) {
    return `${texts.balancedPerformance}: ${texts.configBalanceWorkingTime} (${driver})`
  }
  if (s.traveledDistance === 100) {
    return `${texts.balancedPerformance}: ${texts.configBalanceDistance} (${driver})`
  }
  if (s.load === 100) {
    const loadLabel = texts.configBalanceLoadDistribution(s.loadName ?? '—')
    return `${texts.balancedPerformance}: ${loadLabel} (${driver})`
  }
  return `${texts.balancedPerformance}: ${driver}`
}
